import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Shield, Mail, Phone } from 'lucide-react';
import { Button } from './ui/button';

const EmployeePrivacyField = ({
  value,
  maskedValue,
  type = 'text',
  label = '',
  showToggle = true,
  className = '',
  icon = null,
  autoMask = true
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  // Si pas de valeur, ne rien afficher
  if (!value && !maskedValue) {
    return (
      <span className={`text-muted-foreground italic ${className}`}>
        {t('privacy.notProvided')}
      </span>
    );
  }
  
  // Fonction de masquage automatique si pas de maskedValue fournie
  const autoMaskValue = (val, fieldType) => {
    if (!val) return val;
    
    switch (fieldType) {
      case 'email':
        const emailParts = val.split('@');
        if (emailParts.length !== 2) return val;
        const [username, domain] = emailParts;
        if (username.length <= 2) return val;
        return `${username[0]}${'*'.repeat(username.length - 2)}${username[username.length - 1]}@${domain}`;
      
      case 'phone':
      case 'telephone':
        if (val.length <= 4) return val;
        return `${val.substring(0, 2)}${'*'.repeat(val.length - 4)}${val.substring(val.length - 2)}`;
      
      default:
        // Masquage générique
        if (val.length <= 4) return val;
        return `${val.substring(0, 2)}${'*'.repeat(val.length - 4)}${val.substring(val.length - 2)}`;
    }
  };
  
  // Déterminer la valeur à afficher
  let displayValue;
  if (isVisible) {
    displayValue = value;
  } else {
    // Utiliser maskedValue si fournie, sinon masquer automatiquement
    displayValue = maskedValue || (autoMask ? autoMaskValue(value, type) : value);
  }
  
  // Si pas de masquage nécessaire
  if (!showToggle || value === displayValue) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span>{value}</span>
      </div>
    );
  }
  
  // Icônes par type de champ
  const getFieldIcon = () => {
    if (icon) return icon;
    
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4 text-muted-foreground" />;
      case 'phone':
      case 'telephone':
        return <Phone className="h-4 w-4 text-muted-foreground" />;
      default:
        return null;
    }
  };
  
  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      {/* Icône du type de champ */}
      {getFieldIcon()}
      
      {/* Valeur (masquée ou non) */}
      <span className="flex-1 font-mono text-sm">
        {displayValue}
      </span>
      
      {/* Bouton de basculement */}
      {showToggle && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 hover:bg-muted"
          onClick={() => setIsVisible(!isVisible)}
          title={isVisible ? t('privacy.hide', { label: label.toLowerCase() }) : t('privacy.show', { label: label.toLowerCase() })}
        >
          {isVisible ? (
            <EyeOff className="h-3 w-3" />
          ) : (
            <Eye className="h-3 w-3" />
          )}
        </Button>
      )}
      
      {/* Indicateur RGPD */}
      {!isVisible && showToggle && (
        <div className="flex items-center">
          <Shield className="h-3 w-3 text-blue-500" title={t('privacy.gdprProtected')} />
        </div>
      )}
    </div>
  );
};

// Composants spécialisés pour plus de facilité d'utilisation
export const EmailPrivacyField = ({ email, maskedEmail, className, showToggle = true }) => (
  <EmployeePrivacyField
    value={email}
    maskedValue={maskedEmail}
    type="email"
    label="Email"
    className={className}
    showToggle={showToggle}
  />
);

export const PhonePrivacyField = ({ phone, maskedPhone, className, showToggle = true }) => {
  const { t } = useTranslation();
  return (
    <EmployeePrivacyField
      value={phone}
      maskedValue={maskedPhone}
      type="phone"
      label={t('common.phone')}
      className={className}
      showToggle={showToggle}
    />
  );
};

export default EmployeePrivacyField;
